import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { FileText, ArrowLeft, CreditCard, RefreshCcw, AlertCircle, Mail } from 'lucide-react';

const Terms = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Title */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/20 rounded-2xl mb-4">
              <FileText className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold mb-2">
              Conditions Générales de Vente
            </h1>
            <p className="text-muted">
              Dernière mise à jour : Février 2026
            </p>
          </div>

          <div className="prose prose-invert max-w-none">
            {/* Article 1 */}
            <section className="mb-10">
              <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                <span className="text-primary">Article 1.</span> Objet
              </h2>
              <p className="text-muted leading-relaxed">
                Les présentes Conditions Générales de Vente (CGV) régissent les relations contractuelles 
                entre <strong>Mariposa Consulting LLC</strong>, éditeur de la plateforme Boost-StrategIA, 
                et tout utilisateur souhaitant souscrire à un abonnement payant ou acheter des services 
                sur le site <strong>boost-strategia.com</strong>.
              </p>
              <p className="text-muted leading-relaxed mt-4">
                En souscrivant à un abonnement ou en effectuant un achat, l'utilisateur déclare avoir 
                lu et accepté sans réserve les présentes CGV.
              </p>
            </section>

            {/* Article 2 */}
            <section className="mb-10">
              <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                <span className="text-primary">Article 2.</span> Services proposés
              </h2>
              <p className="text-muted mb-4">
                Boost-StrategIA propose un service de génération de stratégie marketing personnalisée 
                assistée par intelligence artificielle. Les services comprennent :
              </p>
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="font-semibold mb-3">Formules d'abonnement :</h3>
                <ul className="list-disc list-inside text-muted space-y-2">
                  <li><strong>Plan Gratuit</strong> : Accès limité aux 3 premières étapes de l'étude</li>
                  <li><strong>Plan Pro</strong> : 79€/mois ou 790€/an - 2 études complètes par mois + 2 coachings live groupés</li>
                  <li><strong>Plan Agence</strong> : 199€/mois ou 1990€/an - 5 études complètes par mois + 2 coachings live groupés</li>
                </ul>
                
                <h3 className="font-semibold mb-3 mt-6">Services à l'unité :</h3>
                <ul className="list-disc list-inside text-muted space-y-2">
                  <li><strong>Étude supplémentaire</strong> : 29€ par étude</li>
                </ul>
              </div>
            </section>

            {/* Article 3 */}
            <section className="mb-10">
              <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary" />
                <span className="text-primary">Article 3.</span> Prix et paiement
              </h2>
              <p className="text-muted mb-4">
                Les prix sont indiqués en euros (€) TTC. Mariposa Consulting LLC se réserve le droit 
                de modifier ses tarifs à tout moment. Les modifications de prix ne s'appliquent pas 
                aux abonnements en cours.
              </p>
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="font-semibold mb-3">Moyens de paiement acceptés :</h3>
                <ul className="list-disc list-inside text-muted space-y-1">
                  <li>Carte bancaire (Visa, Mastercard, American Express)</li>
                  <li>Prélèvement SEPA</li>
                </ul>
                <p className="text-sm text-muted mt-4">
                  Les paiements sont sécurisés et traités par notre partenaire <strong>Stripe</strong>, 
                  certifié PCI-DSS Level 1.
                </p>
              </div>
            </section>

            {/* Article 4 */}
            <section className="mb-10">
              <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                <RefreshCcw className="w-5 h-5 text-primary" />
                <span className="text-primary">Article 4.</span> Abonnement et renouvellement
              </h2>
              <div className="space-y-4 text-muted">
                <p>
                  <strong>Durée :</strong> Les abonnements sont souscrits pour une durée d'un mois (mensuel) 
                  ou d'un an (annuel) selon le choix de l'utilisateur.
                </p>
                <p>
                  <strong>Renouvellement automatique :</strong> Sauf résiliation, les abonnements sont 
                  automatiquement renouvelés à leur date d'échéance. Le prélèvement est effectué 
                  automatiquement sur le moyen de paiement enregistré.
                </p>
                <p>
                  <strong>Crédits d'études :</strong> Les études non utilisées dans le mois ne sont 
                  pas reportées au mois suivant pour les abonnements mensuels. Pour les abonnements 
                  annuels, le crédit total est disponible sur l'année.
                </p>
              </div>
            </section>

            {/* Article 5 */}
            <section className="mb-10">
              <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-primary" />
                <span className="text-primary">Article 5.</span> Droit de rétractation et résiliation
              </h2>
              <div className="bg-highlight/10 border border-highlight/30 rounded-lg p-6 mb-4">
                <h3 className="font-semibold mb-2">Droit de rétractation (14 jours)</h3>
                <p className="text-sm text-muted">
                  Conformément à l'article L221-18 du Code de la consommation, vous disposez d'un délai 
                  de 14 jours à compter de la souscription pour exercer votre droit de rétractation, 
                  <strong> à condition de ne pas avoir commencé à utiliser le service</strong>.
                </p>
                <p className="text-sm text-muted mt-2">
                  Si vous avez déjà généré une étude ou utilisé le service, le droit de rétractation 
                  ne peut plus s'appliquer conformément à l'article L221-28 du Code de la consommation.
                </p>
              </div>
              
              <h3 className="font-semibold mb-3">Résiliation :</h3>
              <ul className="list-disc list-inside text-muted space-y-2">
                <li>
                  <strong>Abonnement mensuel</strong> : Résiliable à tout moment depuis votre espace client. 
                  La résiliation prend effet à la fin de la période en cours.
                </li>
                <li>
                  <strong>Abonnement annuel</strong> : Résiliable à tout moment. Aucun remboursement 
                  au prorata n'est effectué pour la période restante.
                </li>
              </ul>
            </section>

            {/* Article 6 */}
            <section className="mb-10">
              <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                <span className="text-primary">Article 6.</span> Propriété intellectuelle
              </h2>
              <p className="text-muted mb-4">
                L'utilisateur conserve la pleine propriété des contenus qu'il génère via la plateforme 
                (études marketing, stratégies, textes).
              </p>
              <p className="text-muted">
                La plateforme Boost-StrategIA, son interface, ses algorithmes et sa méthodologie 
                restent la propriété exclusive de Mariposa Consulting LLC. Toute reproduction, 
                modification ou distribution sans autorisation est interdite.
              </p>
            </section>

            {/* Article 7 */}
            <section className="mb-10">
              <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                <span className="text-primary">Article 7.</span> Limitation de responsabilité
              </h2>
              <div className="text-muted space-y-4">
                <p>
                  Boost-StrategIA fournit un outil d'aide à la décision marketing. Les résultats 
                  générés sont des suggestions basées sur l'intelligence artificielle et les 
                  informations fournies par l'utilisateur.
                </p>
                <p>
                  <strong>Mariposa Consulting LLC ne garantit pas :</strong>
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Le succès commercial des stratégies proposées</li>
                  <li>L'exactitude absolue des analyses générées</li>
                  <li>La disponibilité continue et sans interruption du service</li>
                </ul>
                <p>
                  L'utilisateur reste seul responsable de l'utilisation qu'il fait des contenus 
                  générés et des décisions commerciales qu'il prend.
                </p>
              </div>
            </section>

            {/* Article 8 */}
            <section className="mb-10">
              <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                <span className="text-primary">Article 8.</span> Protection des données
              </h2>
              <p className="text-muted">
                La collecte et le traitement des données personnelles sont régis par notre 
                <a href="/privacy" className="text-primary hover:underline ml-1">
                  Politique de Confidentialité
                </a>
                , accessible sur notre site et conforme au Règlement Général sur la Protection 
                des Données (RGPD).
              </p>
            </section>

            {/* Article 9 */}
            <section className="mb-10">
              <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                <span className="text-primary">Article 9.</span> Service client
              </h2>
              <div className="bg-card border border-border rounded-lg p-6">
                <p className="text-muted mb-4">
                  Pour toute question, réclamation ou demande de résiliation, notre service client 
                  est disponible :
                </p>
                <div className="flex items-center gap-2 text-muted">
                  <Mail className="w-4 h-4 text-primary" />
                  <span>contact@boost-strategia.com</span>
                </div>
                <p className="text-sm text-muted mt-4">
                  Nous nous engageons à répondre dans un délai de 48 heures ouvrées.
                </p>
              </div>
            </section>

            {/* Article 10 */}
            <section className="mb-10">
              <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                <span className="text-primary">Article 10.</span> Droit applicable et litiges
              </h2>
              <p className="text-muted mb-4">
                Les présentes CGV sont soumises au droit français. En cas de litige, une solution 
                amiable sera recherchée avant toute action judiciaire.
              </p>
              <p className="text-muted">
                Conformément aux articles L.616-1 et R.616-1 du Code de la consommation, en cas de 
                litige non résolu, le consommateur peut recourir gratuitement au service de médiation 
                MEDICYS (www.medicys.fr).
              </p>
            </section>

            {/* Article 11 */}
            <section>
              <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                <span className="text-primary">Article 11.</span> Informations légales
              </h2>
              <div className="bg-card border border-border rounded-lg p-6 text-muted">
                <p><strong>Éditeur :</strong> Mariposa Consulting LLC</p>
                <p><strong>Email :</strong> contact@boost-strategia.com</p>
                <p><strong>Hébergement :</strong> Emergent Labs</p>
                <p className="mt-4 text-sm">
                  Numéro de TVA intracommunautaire : [À compléter]
                </p>
              </div>
            </section>
          </div>

          {/* Back button */}
          <div className="mt-12 text-center">
            <Button onClick={() => navigate('/pricing')}>
              Voir nos offres
            </Button>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default Terms;
